import {
  AudioEvents,
  GameEvents,
  UIEvents,
} from '@/lib/events/event-catalog.js';

/**
 * # 游戏继续（从暂停恢复）
 *
 * 将游戏从 paused 状态恢复到 playing 状态， 停止暂停特效、播放恢复音效、重新播放背景音乐。
 *
 * ## 限制条件
 *
 * 只能在 `paused` 模式下恢复。以下状态无法继续：
 *
 * - `game-over`：游戏已结束
 * - `main-menu`：主菜单（等级选择界面）
 * - `difficulty`：难度选择界面
 * - `replay`：回放中
 * - `playing`：已在游戏进行中
 *
 * ## 处理流程
 *
 * | 步骤 | 操作            | 说明                         |
 * | ---- | --------------- | ---------------------------- |
 * | 1    | 模式检查        | 非 paused 模式则直接返回     |
 * | 2    | 更新 UI 模式    | 通知 UI 切换回游戏界面       |
 * | 3    | 更新 Store 模式 | 将游戏模式设为 playing       |
 * | 4    | 停止暂停动画    | 移除暂停特效                 |
 * | 5    | 播放恢复音效    | 播放 RESUME 音效             |
 * | 6    | 恢复背景音乐    | 使用暂停前的等级播放对应曲目 |
 *
 * ## 调用时机
 *
 * - 玩家按 P 键（键盘）
 * - 玩家按 Y 键（手柄）
 * - AI 执行 TOGGLE_PAUSED 命令
 *
 * @function resume
 * @param {object} runtime - 游戏运行时对象
 * @returns {void}
 */
const resume = (runtime) => {
  const { id, Store } = runtime;
  const mode = Store.getMode();

  /**
   * ======== 步骤 1：模式检查 ========
   *
   * 只有 paused 模式可以恢复。 游戏结束、菜单界面等状态下禁止继续。
   */
  if (mode !== 'paused') {
    return;
  }

  /**
   * ======== 获取暂停前的等级 ========
   *
   * 用于恢复背景音乐时选取对应曲目。
   */
  const level = Store.getLevel();
  const AE = AudioEvents();
  const GE = GameEvents(id);
  const UE = UIEvents(id);

  /**
   * ======== 步骤 2：更新 UI 模式 ========
   *
   * 通知 UI 层切换回游戏进行界面。
   */
  runtime.emit(UE.UPDATE_MODE, { mode: 'playing' });

  /**
   * ======== 步骤 3：更新 Store 模式 ========
   *
   * 将游戏状态切换回 playing，恢复下落和输入处理。
   */
  Store.setMode('playing');

  /**
   * ======== 步骤 4：停止暂停动画 ========
   *
   * 移除暂停特效（画面变暗、暂停图标、滴答音效等）。
   */
  runtime.emit(GE.STOP_PAUSED);

  /** ======== 步骤 5：播放恢复音效 ======== */
  runtime.emit(AE.PLAY_SOUND, { sound: 'RESUME' });

  /**
   * ======== 步骤 6：恢复背景音乐 ========
   *
   * 使用暂停前的等级选取对应曲目。
   */
  runtime.emit(AE.RESUME_BGM, { level });
};

export default resume;
